import { useState, useRef, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../components/LanguageProvider";
import jsPDF from "jspdf";

// Mock graph data
const initialNodes = [
  { id: 1, label: "John Smith", group: "person", properties: { role: "CEO", company: "TechCorp" } },
  { id: 2, label: "Sarah Johnson", group: "person", properties: { role: "CTO", company: "TechCorp" } },
  { id: 3, label: "TechCorp Inc.", group: "organization", properties: { industry: "Technology", founded: 2015 } },
  { id: 4, label: "DataFlow Ltd.", group: "organization", properties: { industry: "Data Analytics", founded: 2018 } },
  { id: 5, label: "New York", group: "location", properties: { type: "City", country: "USA" } },
  { id: 6, label: "Annual Summit 2024", group: "event", properties: { date: "2024-06-15", attendees: 500 } },
  { id: 7, label: "Michael Chen", group: "person", properties: { role: "Investor", fund: "Capital V" } },
  { id: 8, label: "Capital Ventures", group: "organization", properties: { type: "VC Firm", aum: "$2B" } },
  { id: 9, label: "San Francisco", group: "location", properties: { type: "City", country: "USA" } },
  { id: 10, label: "Merger Deal", group: "event", properties: { date: "2024-03-01", value: "$50M" } },
];

const initialEdges = [
  { from: 1, to: 3, label: "CEO of" },
  { from: 2, to: 3, label: "CTO of" },
  { from: 1, to: 2, label: "works with" },
  { from: 3, to: 5, label: "headquartered in" },
  { from: 1, to: 6, label: "attended" },
  { from: 2, to: 6, label: "attended" },
  { from: 7, to: 8, label: "partner at" },
  { from: 8, to: 3, label: "invested in" },
  { from: 8, to: 4, label: "invested in" },
  { from: 4, to: 9, label: "located in" },
  { from: 3, to: 10, label: "involved in" },
  { from: 4, to: 10, label: "involved in" },
  { from: 7, to: 10, label: "facilitated" },
];

const groupColors = {
  person: { bg: "hsl(182, 72%, 45%)", text: "#fff" },
  organization: { bg: "hsl(205, 80%, 55%)", text: "#fff" },
  location: { bg: "hsl(165, 65%, 45%)", text: "#fff" },
  event: { bg: "hsl(38, 92%, 50%)", text: "#000" },
};

// SVG-based graph with draggable nodes and icons
const GraphCanvas = ({ nodes, edges, selectedNode, selectedEdge, hoveredNode, hoveredEdge, onNodeSelect, onNodeHover, onEdgeSelect, onEdgeHover, onNodeMove, zoom, offset }) => {
  const [nodePositions, setNodePositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  // Initialize positions
  useEffect(() => {
    const centerX = 400;
    const centerY = 300;
    const radius = 200;
    
    setNodePositions(prev => {
      const newPositions = { ...prev };
      nodes.forEach((node, index) => {
        // Only set initial position if not already set
        if (!newPositions[node.id]) {
          const angle = (2 * Math.PI * index) / nodes.length;
          const seed = typeof node.id === 'string' ? node.id.charCodeAt(0) * 0.1 : node.id * 0.1;
          newPositions[node.id] = {
            x: centerX + radius * Math.cos(angle) * (0.9 + Math.sin(seed) * 0.1),
            y: centerY + radius * Math.sin(angle) * (0.9 + Math.cos(seed) * 0.1),
          };
        }
      });
      return newPositions;
    });
  }, [nodes]);

  const getNodeRadius = (group) => {
    switch (group) {
      case "organization": return 35;
      case "event": return 30;
      default: return 28;
    }
  };

  // SVG path data for icons (from Font Awesome 6 - Solid)
  const iconPaths = {
    person: "M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z",
    organization: "M48 0C21.5 0 0 21.5 0 48V464c0 26.5 21.5 48 48 48h96V432c0-26.5 21.5-48 48-48s48 21.5 48 48v80h96c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48H48zM64 240c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V240zm112-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H176c-8.8 0-16-7.2-16-16V240c0-8.8 7.2-16 16-16zm80 16c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H272c-8.8 0-16-7.2-16-16V240zM80 96h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16zm80 16c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H176c-8.8 0-16-7.2-16-16V112zM272 96h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H272c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16z",
    location: "M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z",
    event: "M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336zM64 400v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H208zm112 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H336c-8.8 0-16 7.2-16 16z",
  };
  
  const iconViewBoxes = {
    person: "0 0 448 512",
    organization: "0 0 384 512",
    location: "0 0 384 512",
    event: "0 0 448 512",
  };

  const getNodeIcon = (group) => {
    return iconPaths[group] || iconPaths.person;
  };
  
  const getIconViewBox = (group) => {
    return iconViewBoxes[group] || "0 0 448 512";
  };

  // Get SVG point from mouse/touch event
  const getSVGPoint = (e) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    pt.x = clientX;
    pt.y = clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: svgP.x, y: svgP.y };
  };

  const handleMouseDown = (e, node) => {
    e.stopPropagation();
    const svgPoint = getSVGPoint(e);
    setDragging(node.id);
    setDragStart({
      x: svgPoint.x - (nodePositions[node.id]?.x || 0),
      y: svgPoint.y - (nodePositions[node.id]?.y || 0),
    });
  };

  // Touch start handler
  const handleTouchStart = (e, node) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent scrolling while dragging
    const svgPoint = getSVGPoint(e);
    setDragging(node.id);
    setDragStart({
      x: svgPoint.x - (nodePositions[node.id]?.x || 0),
      y: svgPoint.y - (nodePositions[node.id]?.y || 0),
    });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const svgPoint = getSVGPoint(e);
    setNodePositions(prev => ({
      ...prev,
      [dragging]: {
        x: svgPoint.x - dragStart.x,
        y: svgPoint.y - dragStart.y,
      },
    }));
  };

  // Touch move handler
  const handleTouchMove = (e) => {
    if (!dragging) return;
    e.preventDefault(); // Prevent scrolling while dragging
    const svgPoint = getSVGPoint(e);
    setNodePositions(prev => ({
      ...prev,
      [dragging]: {
        x: svgPoint.x - dragStart.x,
        y: svgPoint.y - dragStart.y,
      },
    }));
  };

  const handleMouseUp = () => {
    if (dragging && onNodeMove) {
      onNodeMove(dragging, nodePositions[dragging]);
    }
    setDragging(null);
  };

  // Touch end handler
  const handleTouchEnd = () => {
    if (dragging && onNodeMove) {
      onNodeMove(dragging, nodePositions[dragging]);
    }
    setDragging(null);
  };

  return (
    <svg 
      ref={svgRef}
      width="100%" 
      height="100%" 
      viewBox="0 0 800 600" 
      className="bg-transparent touch-none"
      style={{ 
        transform: `scale(${zoom / 100}) translate(${offset.x}px, ${offset.y}px)`, 
        transformOrigin: "center" 
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <defs>
        <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="hsl(var(--muted-foreground))" />
        </marker>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id="hoverGlow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id="dropShadow">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3"/>
        </filter>
      </defs>

      {/* Edges */}
      <g>
        {edges.map((edge, idx) => {
          const fromPos = nodePositions[edge.from];
          const toPos = nodePositions[edge.to];
          if (!fromPos || !toPos) return null;
          
          const fromNode = nodes.find(n => n.id === edge.from);
          const toNode = nodes.find(n => n.id === edge.to);
          const fromRadius = getNodeRadius(fromNode?.group);
          const toRadius = getNodeRadius(toNode?.group);
          
          const dx = toPos.x - fromPos.x;
          const dy = toPos.y - fromPos.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist === 0) return null;
          
          const startX = fromPos.x + (dx / dist) * fromRadius;
          const startY = fromPos.y + (dy / dist) * fromRadius;
          const endX = toPos.x - (dx / dist) * (toRadius + 8);
          const endY = toPos.y - (dy / dist) * (toRadius + 8);
          const midX = (startX + endX) / 2;
          const midY = (startY + endY) / 2;
          
          const isSelected = selectedEdge?.from === edge.from && selectedEdge?.to === edge.to;
          const isHovered = hoveredEdge?.from === edge.from && hoveredEdge?.to === edge.to;
          
          return (
            <g 
              key={idx}
              onClick={(e) => { e.stopPropagation(); onEdgeSelect(edge); }}
              onMouseEnter={() => onEdgeHover(edge)}
              onMouseLeave={() => onEdgeHover(null)}
              style={{ cursor: "pointer" }}
            >
              {/* Invisible wider line for easier clicking */}
              <line 
                x1={startX} y1={startY} x2={endX} y2={endY} 
                stroke="transparent" 
                strokeWidth="20"
              />
              {/* Visible line */}
              <line 
                x1={startX} y1={startY} x2={endX} y2={endY} 
                stroke={isSelected ? "hsl(var(--primary))" : isHovered ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"} 
                strokeWidth={isSelected ? "3" : isHovered ? "2.5" : "1.5"} 
                strokeOpacity={isSelected ? "1" : isHovered ? "0.8" : "0.5"} 
                markerEnd="url(#arrowhead)"
                style={{ transition: "all 0.2s ease" }}
              />
              {/* Label background */}
              {(isHovered || isSelected) && (
                <rect
                  x={midX - (edge.label.length * 3.5) - 4}
                  y={midY - 18}
                  width={(edge.label.length * 7) + 8}
                  height="16"
                  rx="4"
                  fill="hsl(var(--card))"
                  stroke={isSelected ? "hsl(var(--primary))" : "hsl(var(--border))"}
                  strokeWidth="1"
                />
              )}
              <text 
                x={midX} y={midY - 7} 
                textAnchor="middle" 
                fill={isSelected ? "hsl(var(--primary))" : isHovered ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))"} 
                fontSize={isHovered || isSelected ? "11" : "10"} 
                fontFamily="Inter"
                fontWeight={isSelected ? "600" : "400"}
                style={{ transition: "all 0.2s ease" }}
              >
                {edge.label}
              </text>
            </g>
          );
        })}
      </g>

      {/* Nodes */}
      <g>
        {nodes.map((node) => {
          const pos = nodePositions[node.id];
          if (!pos) return null;
          
          const isSelected = selectedNode?.id === node.id;
          const isHovered = hoveredNode?.id === node.id;
          const isDragging = dragging === node.id;
          const radius = getNodeRadius(node.group);
          const colors = groupColors[node.group] || { bg: "hsl(var(--primary))", text: "#fff" };
          const scale = isHovered || isDragging ? 1.15 : 1;
          
          return (
            <g
              key={node.id}
              transform={`translate(${pos.x}, ${pos.y}) scale(${scale})`}
              onClick={() => !isDragging && onNodeSelect(node)}
              onMouseEnter={() => onNodeHover(node)}
              onMouseLeave={() => !isDragging && onNodeHover(null)}
              onMouseDown={(e) => handleMouseDown(e, node)}
              onTouchStart={(e) => handleTouchStart(e, node)}
              style={{ 
                cursor: isDragging ? "grabbing" : "grab", 
                transition: isDragging ? "none" : "transform 0.2s ease",
                touchAction: "none"
              }}
              filter={isDragging ? "url(#dropShadow)" : undefined}
            >
              {/* Hover glow ring */}
              {(isHovered || isDragging) && !isSelected && (
                <circle 
                  r={radius + 6} 
                  fill="none" 
                  stroke={colors.bg} 
                  strokeWidth="3" 
                  strokeOpacity="0.4" 
                  filter="url(#hoverGlow)"
                />
              )}
              
              {/* Selection ring */}
              {isSelected && (
                <circle 
                  r={radius + 8} 
                  fill="none" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth="3" 
                  strokeDasharray="5,3" 
                  filter="url(#glow)"
                >
                  <animateTransform 
                    attributeName="transform" 
                    type="rotate" 
                    from="0" to="360" 
                    dur="8s" 
                    repeatCount="indefinite"
                  />
                </circle>
              )}
              
              {/* Node circle with gradient */}
              <circle 
                r={radius} 
                fill={colors.bg} 
                stroke={isSelected || isHovered || isDragging ? "white" : "rgba(255,255,255,0.3)"} 
                strokeWidth={isSelected || isDragging ? "3" : "2"}
              />
              
              {/* Icon using SVG path */}
              <g 
                transform={`translate(${-radius * 0.35}, ${-radius * 0.4}) scale(${radius * 0.0018})`}
                style={{ pointerEvents: "none" }}
              >
                <path 
                  d={getNodeIcon(node.group)} 
                  fill={colors.text}
                />
              </g>
              
              {/* Cluster badge */}
              {node.isCluster && (
                <g transform={`translate(${radius * 0.7}, ${-radius * 0.7})`}>
                  <circle r="12" fill="hsl(var(--destructive))" stroke="white" strokeWidth="2"/>
                  <text 
                    textAnchor="middle" 
                    dominantBaseline="middle" 
                    fill="white" 
                    fontSize="10" 
                    fontWeight="bold"
                  >
                    {node.clusterCount}
                  </text>
                </g>
              )}
              
              {/* Label */}
              <text 
                y={radius + 16} 
                textAnchor="middle" 
                fill="hsl(var(--foreground))" 
                fontSize="11" 
                fontFamily="Inter" 
                fontWeight="500"
                style={{ pointerEvents: "none" }}
              >
                {node.label.length > 15 ? node.label.substring(0, 15) + "..." : node.label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default function GraphExplorer() {
  const { t } = useLanguage();
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [zoom, setZoom] = useState([100]);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleGroups, setVisibleGroups] = useState({ person: true, organization: true, location: true, event: true });
  const [tool, setTool] = useState("select"); // select, pan
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editEdgeDialogOpen, setEditEdgeDialogOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [editingEdge, setEditingEdge] = useState(null);
  const [clusteredGroups, setClusteredGroups] = useState({});
  const [importedNodes, setImportedNodes] = useState([]);
  const [showImportBanner, setShowImportBanner] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobileNodeTypesOpen, setMobileNodeTypesOpen] = useState(false);

  // Fullscreen handler
  const handleFullscreen = () => {
    const container = document.querySelector('.graph-explorer-container');
    if (!document.fullscreenElement) {
      container?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Check for imported nodes from Search
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('import') === 'true') {
      const importData = sessionStorage.getItem('graphImportData');
      if (importData) {
        try {
          const parsedData = JSON.parse(importData);
          // Generate unique IDs and add to nodes
          const newNodes = parsedData.map((item, index) => ({
            id: `imported-${Date.now()}-${index}`,
            label: item.label,
            group: item.group,
            properties: item.properties || {},
            isImported: true
          }));
          setNodes(prev => [...prev, ...newNodes]);
          setImportedNodes(newNodes);
          setShowImportBanner(true);
          // Clear the import data
          sessionStorage.removeItem('graphImportData');
          // Clean up URL
          window.history.replaceState({}, '', '/graph');
          // Auto-hide banner after 5 seconds
          setTimeout(() => setShowImportBanner(false), 5000);
        } catch (e) {
          console.error('Error importing graph data:', e);
        }
      }
    }
  }, []);

  const filteredNodes = nodes.filter(node => {
    if (!visibleGroups[node.group]) return false;
    if (searchQuery && !node.label.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    // Hide nodes that are in a clustered group
    if (clusteredGroups[node.group]) return false;
    return true;
  });

  // Generate cluster nodes for collapsed groups
  const clusterNodes = Object.entries(clusteredGroups)
    .filter(([_, isClustered]) => isClustered)
    .map(([group]) => {
      const groupNodes = nodes.filter(n => n.group === group && visibleGroups[group]);
      return {
        id: `cluster-${group}`,
        label: `${groupNodes.length} ${t(group)}s`,
        group: group,
        isCluster: true,
        clusterCount: groupNodes.length,
        properties: { count: groupNodes.length, type: "cluster" }
      };
    });

  const displayNodes = [...filteredNodes, ...clusterNodes];

  const filteredEdges = edges.filter(edge => {
    return filteredNodes.some(n => n.id === edge.from) && filteredNodes.some(n => n.id === edge.to);
  });

  const getConnectedNodes = (nodeId) => {
    const connectedIds = new Set();
    edges.forEach(edge => {
      if (edge.from === nodeId) connectedIds.add(edge.to);
      if (edge.to === nodeId) connectedIds.add(edge.from);
    });
    return nodes.filter(n => connectedIds.has(n.id));
  };

  // Edit node handler
  const handleEditNode = (node) => {
    setEditingNode({ ...node, properties: { ...node.properties } });
    setEditDialogOpen(true);
  };

  // Save edited node
  const handleSaveNode = () => {
    if (!editingNode) return;
    setNodes(nodes.map(n => n.id === editingNode.id ? editingNode : n));
    setSelectedNode(editingNode);
    setEditDialogOpen(false);
    setEditingNode(null);
  };

  // Update editing node property
  const updateEditingProperty = (key, value) => {
    setEditingNode({
      ...editingNode,
      properties: { ...editingNode.properties, [key]: value }
    });
  };

  // Add new property
  const addProperty = () => {
    const key = `property${Object.keys(editingNode.properties).length + 1}`;
    updateEditingProperty(key, "");
  };

  // Delete property
  const deleteProperty = (key) => {
    const newProps = { ...editingNode.properties };
    delete newProps[key];
    setEditingNode({ ...editingNode, properties: newProps });
  };

  // Edit edge handler
  const handleEditEdge = (edge) => {
    setEditingEdge({ ...edge });
    setEditEdgeDialogOpen(true);
  };

  // Save edited edge
  const handleSaveEdge = () => {
    if (!editingEdge) return;
    setEdges(edges.map(e => 
      (e.from === editingEdge.from && e.to === editingEdge.to) ? editingEdge : e
    ));
    setSelectedEdge(editingEdge);
    setEditEdgeDialogOpen(false);
    setEditingEdge(null);
  };

  // Delete edge
  const handleDeleteEdge = () => {
    if (!editingEdge) return;
    setEdges(edges.filter(e => !(e.from === editingEdge.from && e.to === editingEdge.to)));
    setSelectedEdge(null);
    setEditEdgeDialogOpen(false);
    setEditingEdge(null);
  };

  // Toggle cluster for a group
  const toggleCluster = (group) => {
    setClusteredGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Expand cluster
  const expandCluster = (group) => {
    setClusteredGroups(prev => ({ ...prev, [group]: false }));
  };

  const handleZoomIn = () => setZoom([Math.min(zoom[0] + 20, 200)]);
  const handleZoomOut = () => setZoom([Math.max(zoom[0] - 20, 50)]);
  const handleFitToScreen = () => {
    setZoom([100]);
    setOffset({ x: 0, y: 0 });
  };

  const handleExportPDF = () => {
    const pdf = new jsPDF('landscape');
    pdf.setFontSize(20);
    pdf.text('Graph Explorer Report', 20, 20);
    pdf.setFontSize(12);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
    pdf.text(`Total Nodes: ${filteredNodes.length}`, 20, 45);
    pdf.text(`Total Edges: ${filteredEdges.length}`, 20, 55);
    
    pdf.setFontSize(14);
    pdf.text('Nodes:', 20, 75);
    
    let yPos = 85;
    filteredNodes.forEach((node, idx) => {
      if (yPos > 180) return;
      pdf.setFontSize(10);
      pdf.text(`${idx + 1}. ${node.label} (${node.group})`, 25, yPos);
      yPos += 7;
    });
    
    pdf.save('graph-explorer-report.pdf');
  };

  return (
    <TooltipProvider>
    <div className="graph-explorer-container flex flex-col lg:flex-row h-[calc(100vh-3.5rem)] lg:h-[calc(100vh-4rem)] bg-background">
      {/* Import Banner */}
      <AnimatePresence>
        {showImportBanner && importedNodes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="absolute top-0 left-0 right-0 z-50 bg-primary/90 text-primary-foreground px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                <i className="fa-solid fa-circle-check"></i>
              </div>
              <div>
                <p className="font-semibold text-sm">{importedNodes.length} {t("entitiesImported") || "entities imported"}</p>
                <p className="text-xs opacity-80">{t("entitiesAddedToGraph") || "Entities have been added to the graph"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="sm" 
                className="h-7"
                onClick={() => {
                  // Select first imported node
                  if (importedNodes[0]) setSelectedNode(importedNodes[0]);
                }}
              >
                <i className="fa-solid fa-eye mr-2"></i>
                {t("view")}
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 text-white hover:bg-white/20"
                onClick={() => setShowImportBanner(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Left Panel */}
      <div className="w-full lg:w-72 border-b lg:border-b-0 lg:border-r border-border bg-card/50 flex flex-col max-h-[40vh] lg:max-h-none">
        <div className="p-3 lg:p-4 border-b border-border">
          <div className="relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
            <Input 
              placeholder={t("searchNodes")} 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className="pl-10 h-9 text-sm"
            />
          </div>
        </div>

        <ScrollArea className="flex-1 p-3 lg:p-4">
          <div className="space-y-4">
            {/* Show imported nodes section if any */}
            {importedNodes.length > 0 && (
              <>
                <div className="space-y-2">
                  <h4 className="text-xs lg:text-sm font-medium text-foreground flex items-center gap-2">
                    <i className="fa-solid fa-file-import text-primary"></i>
                    {t("importedEntities") || "Imported Entities"}
                    <Badge variant="secondary" className="text-xs ml-auto">{importedNodes.length}</Badge>
                  </h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {importedNodes.map((node) => (
                      <div 
                        key={node.id} 
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors text-xs",
                          selectedNode?.id === node.id ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/50"
                        )}
                        onClick={() => setSelectedNode(node)}
                      >
                        <div 
                          className="h-5 w-5 rounded-full flex items-center justify-center shrink-0" 
                          style={{ backgroundColor: `${groupColors[node.group]?.bg}30` }}
                        >
                          <i className={cn(
                            "fa-solid text-[8px]",
                            node.group === "person" && "fa-user",
                            node.group === "organization" && "fa-building",
                            node.group === "location" && "fa-location-dot",
                            node.group === "event" && "fa-calendar"
                          )} style={{ color: groupColors[node.group]?.bg }}></i>
                        </div>
                        <span className="truncate">{node.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Node Types - Collapsible on Mobile */}
            <div className="space-y-3">
              {/* Mobile: Toggle button for Node Types */}
              <button
                className="lg:hidden w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                onClick={() => setMobileNodeTypesOpen(!mobileNodeTypesOpen)}
                data-testid="mobile-node-types-toggle"
              >
                <span className="text-xs font-medium text-foreground flex items-center gap-2">
                  <i className="fa-solid fa-filter text-primary"></i>
                  {t("nodeTypes")}
                </span>
                <i className={cn(
                  "fa-solid text-xs text-muted-foreground transition-transform duration-200",
                  mobileNodeTypesOpen ? "fa-chevron-up" : "fa-chevron-down"
                )}></i>
              </button>

              {/* Desktop: Always visible header */}
              <h4 className="hidden lg:flex text-xs lg:text-sm font-medium text-foreground items-center gap-2">
                <i className="fa-solid fa-filter text-primary"></i>
                {t("nodeTypes")}
              </h4>

              {/* Node Types content - always visible on desktop, toggle on mobile */}
              <div className={cn(
                "space-y-3 overflow-hidden transition-all duration-200",
                "lg:block lg:max-h-none lg:opacity-100",
                mobileNodeTypesOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0 lg:max-h-none lg:opacity-100"
              )}>
                {Object.entries(groupColors).map(([group, colors]) => (
                  <div key={group} className="flex items-center space-x-2">
                    <Checkbox 
                      id={group} 
                      checked={visibleGroups[group]} 
                      onCheckedChange={(checked) => setVisibleGroups({ ...visibleGroups, [group]: checked })}
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: colors.bg }}></div>
                      <Label htmlFor={group} className="capitalize text-xs lg:text-sm cursor-pointer">{t(group)}</Label>
                    </div>
                    <Badge variant="outline" className="text-xs">{nodes.filter(n => n.group === group).length}</Badge>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => toggleCluster(group)}
                        >
                          <i className={cn("fa-solid text-xs", clusteredGroups[group] ? "fa-expand" : "fa-compress")}></i>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{clusteredGroups[group] ? t("expand") : t("cluster")}</TooltipContent>
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="p-3 lg:p-4 border-t border-border">
          <div className="flex items-center justify-between text-xs lg:text-sm text-muted-foreground">
            <span>{filteredNodes.length} {t("nodes")}</span>
            <span>{filteredEdges.length} {t("edges")}</span>
          </div>
        </div>
      </div>

      {/* Main Graph Canvas */}
      <div className="flex-1 relative bg-muted/20 min-h-[300px]">
        <GraphCanvas 
          nodes={displayNodes} 
          edges={filteredEdges} 
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          hoveredNode={hoveredNode}
          hoveredEdge={hoveredEdge}
          onNodeSelect={(node) => {
            if (node?.isCluster) {
              expandCluster(node.group);
            } else {
              setSelectedNode(node);
              setSelectedEdge(null);
            }
          }} 
          onNodeHover={setHoveredNode}
          onEdgeSelect={(edge) => {
            setSelectedEdge(edge);
            setSelectedNode(null);
          }}
          onEdgeHover={setHoveredEdge}
          zoom={zoom[0]}
          offset={offset}
        />

        {/* Floating Toolbar */}
        <TooltipProvider>
          <div className="absolute top-2 sm:top-4 right-2 sm:right-4 flex flex-col gap-1 p-1.5 sm:p-2 rounded-lg bg-card/95 backdrop-blur-sm border border-border z-10">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant={tool === "select" ? "default" : "ghost"} 
                  size="icon" 
                  className="h-7 w-7 sm:h-8 sm:w-8"
                  onClick={() => setTool("select")}
                >
                  <i className="fa-solid fa-arrow-pointer text-xs sm:text-sm"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t("select")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant={tool === "pan" ? "default" : "ghost"} 
                  size="icon" 
                  className="h-7 w-7 sm:h-8 sm:w-8"
                  onClick={() => setTool("pan")}
                >
                  <i className="fa-solid fa-hand text-xs sm:text-sm"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t("pan")}</TooltipContent>
            </Tooltip>
            <Separator className="my-1" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={handleZoomIn}>
                  <i className="fa-solid fa-plus text-xs sm:text-sm"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t("zoomIn")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={handleZoomOut}>
                  <i className="fa-solid fa-minus text-xs sm:text-sm"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t("zoomOut")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={handleFitToScreen}>
                  <i className="fa-solid fa-arrows-to-dot text-xs sm:text-sm"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t("fitToScreen")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={handleFullscreen}>
                  <i className={cn("fa-solid text-xs sm:text-sm", isFullscreen ? "fa-compress" : "fa-expand")}></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t("fullscreen")}</TooltipContent>
            </Tooltip>
            <Separator className="my-1" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={handleExportPDF}>
                  <i className="fa-solid fa-file-pdf text-xs sm:text-sm"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t("export")} PDF</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>

        {/* Stats - Bottom Left */}
        <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-card/95 backdrop-blur-sm border border-border">
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <span className="text-muted-foreground">{t("nodes")}:</span>
              <span className="font-semibold">{filteredNodes.length}</span>
            </div>
            <Separator orientation="vertical" className="h-3 sm:h-4" />
            <div className="flex items-center gap-1 sm:gap-2">
              <span className="text-muted-foreground">{t("edges")}:</span>
              <span className="font-semibold">{filteredEdges.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Node Details */}
      <AnimatePresence>
        {selectedNode && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            transition={{ duration: 0.2 }} 
            className="fixed lg:relative inset-x-0 top-14 bottom-0 lg:top-auto lg:bottom-auto lg:inset-x-auto w-full lg:w-auto border-l border-border bg-card overflow-hidden z-30"
          >
            <div className="w-full lg:w-80 h-full flex flex-col">
              <div className="p-3 sm:p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* Mobile back button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8 lg:hidden flex items-center gap-2" 
                      onClick={() => setSelectedNode(null)}
                      data-testid="mobile-node-details-close"
                    >
                      <i className="fa-solid fa-arrow-left text-sm"></i>
                      <span className="text-xs">{t("back") || "Back"}</span>
                    </Button>
                    <h3 className="font-semibold text-foreground text-sm sm:text-base">{t("nodeDetails")}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={() => handleEditNode(selectedNode)}>
                      <i className="fa-solid fa-pen text-xs sm:text-sm"></i>
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 hidden lg:flex" onClick={() => setSelectedNode(null)}>
                      <i className="fa-solid fa-xmark text-sm"></i>
                    </Button>
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1 p-3 sm:p-4">
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div 
                      className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl" 
                      style={{ backgroundColor: groupColors[selectedNode.group]?.bg }}
                    >
                      <i className={cn(
                        "fa-solid text-lg sm:text-xl text-white", 
                        selectedNode.group === "person" && "fa-user", 
                        selectedNode.group === "organization" && "fa-building", 
                        selectedNode.group === "location" && "fa-location-dot", 
                        selectedNode.group === "event" && "fa-calendar"
                      )}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-sm sm:text-base">{selectedNode.label}</h4>
                      <Badge 
                        variant="outline" 
                        className="mt-1 capitalize text-xs" 
                        style={{ 
                          backgroundColor: `${groupColors[selectedNode.group]?.bg}20`, 
                          borderColor: groupColors[selectedNode.group]?.bg 
                        }}
                      >
                        {t(selectedNode.group)}
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2 sm:space-y-3">
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("properties")}</h5>
                    {Object.entries(selectedNode.properties).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-muted/50">
                        <span className="text-xs sm:text-sm text-muted-foreground capitalize">{key}</span>
                        <span className="text-xs sm:text-sm font-medium">{value}</span>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className="space-y-2 sm:space-y-3">
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t("connected")} ({getConnectedNodes(selectedNode.id).length})
                    </h5>
                    <div className="space-y-2">
                      {getConnectedNodes(selectedNode.id).slice(0, 5).map((node) => (
                        <div 
                          key={node.id} 
                          onClick={() => setSelectedNode(node)} 
                          className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                        >
                          <div 
                            className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-lg" 
                            style={{ backgroundColor: `${groupColors[node.group]?.bg}30` }}
                          >
                            <i 
                              className={cn(
                                "fa-solid text-xs sm:text-sm", 
                                node.group === "person" && "fa-user", 
                                node.group === "organization" && "fa-building", 
                                node.group === "location" && "fa-location-dot", 
                                node.group === "event" && "fa-calendar"
                              )} 
                              style={{ color: groupColors[node.group]?.bg }}
                            ></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium truncate">{node.label}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <div className="p-3 sm:p-4 border-t border-border">
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 text-xs sm:text-sm h-8 sm:h-9">
                    <i className="fa-solid fa-expand mr-1 sm:mr-2"></i>
                    {t("expand")}
                  </Button>
                  <Button className="flex-1 text-xs sm:text-sm h-8 sm:h-9">
                    <i className="fa-solid fa-route mr-1 sm:mr-2"></i>
                    {t("paths")}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right Panel - Edge Details */}
      <AnimatePresence>
        {selectedEdge && !selectedNode && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            transition={{ duration: 0.2 }} 
            className="fixed lg:relative inset-x-0 top-14 bottom-0 lg:top-auto lg:bottom-auto lg:inset-x-auto w-full lg:w-auto border-l border-border bg-card overflow-hidden z-30"
          >
            <div className="w-full lg:w-80 h-full flex flex-col">
              <div className="p-3 sm:p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* Mobile back button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8 lg:hidden flex items-center gap-2" 
                      onClick={() => setSelectedEdge(null)}
                      data-testid="mobile-edge-details-close"
                    >
                      <i className="fa-solid fa-arrow-left text-sm"></i>
                      <span className="text-xs">{t("back") || "Back"}</span>
                    </Button>
                    <h3 className="font-semibold text-foreground text-sm sm:text-base">{t("relationDetails") || "Relation Details"}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={() => handleEditEdge(selectedEdge)}>
                      <i className="fa-solid fa-pen text-xs sm:text-sm"></i>
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 hidden lg:flex" onClick={() => setSelectedEdge(null)}>
                      <i className="fa-solid fa-xmark text-sm"></i>
                    </Button>
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1 p-3 sm:p-4">
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl bg-primary/10">
                      <i className="fa-solid fa-link text-lg sm:text-xl text-primary"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-sm sm:text-base">{selectedEdge.label}</h4>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {t("relation") || "Relation"}
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("connection") || "Connection"}</h5>
                    
                    {/* From Node */}
                    <div 
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => {
                        const fromNode = nodes.find(n => n.id === selectedEdge.from);
                        if (fromNode) {
                          setSelectedNode(fromNode);
                          setSelectedEdge(null);
                        }
                      }}
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${groupColors[nodes.find(n => n.id === selectedEdge.from)?.group]?.bg}30` }}>
                        <i className={cn(
                          "fa-solid text-sm",
                          nodes.find(n => n.id === selectedEdge.from)?.group === "person" && "fa-user",
                          nodes.find(n => n.id === selectedEdge.from)?.group === "organization" && "fa-building",
                          nodes.find(n => n.id === selectedEdge.from)?.group === "location" && "fa-location-dot",
                          nodes.find(n => n.id === selectedEdge.from)?.group === "event" && "fa-calendar"
                        )} style={{ color: groupColors[nodes.find(n => n.id === selectedEdge.from)?.group]?.bg }}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">{t("source") || "From"}</p>
                        <p className="text-sm font-medium">{nodes.find(n => n.id === selectedEdge.from)?.label}</p>
                      </div>
                    </div>

                    <div className="flex justify-center">
                      <i className="fa-solid fa-arrow-down text-muted-foreground"></i>
                    </div>

                    {/* To Node */}
                    <div 
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => {
                        const toNode = nodes.find(n => n.id === selectedEdge.to);
                        if (toNode) {
                          setSelectedNode(toNode);
                          setSelectedEdge(null);
                        }
                      }}
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${groupColors[nodes.find(n => n.id === selectedEdge.to)?.group]?.bg}30` }}>
                        <i className={cn(
                          "fa-solid text-sm",
                          nodes.find(n => n.id === selectedEdge.to)?.group === "person" && "fa-user",
                          nodes.find(n => n.id === selectedEdge.to)?.group === "organization" && "fa-building",
                          nodes.find(n => n.id === selectedEdge.to)?.group === "location" && "fa-location-dot",
                          nodes.find(n => n.id === selectedEdge.to)?.group === "event" && "fa-calendar"
                        )} style={{ color: groupColors[nodes.find(n => n.id === selectedEdge.to)?.group]?.bg }}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">{t("target") || "To"}</p>
                        <p className="text-sm font-medium">{nodes.find(n => n.id === selectedEdge.to)?.label}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <div className="p-3 sm:p-4 border-t border-border">
                <Button className="w-full text-xs sm:text-sm h-8 sm:h-9" onClick={() => handleEditEdge(selectedEdge)}>
                  <i className="fa-solid fa-pen mr-2"></i>
                  {t("editRelation") || "Edit Relation"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Node Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fa-solid fa-pen text-primary"></i>
              {t("editNode")}
            </DialogTitle>
            <DialogDescription>
              {t("editNodeDescription")}
            </DialogDescription>
          </DialogHeader>
          
          {editingNode && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="node-label">{t("name")}</Label>
                <Input 
                  id="node-label" 
                  value={editingNode.label} 
                  onChange={(e) => setEditingNode({ ...editingNode, label: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="node-type">{t("type")}</Label>
                <Select 
                  value={editingNode.group} 
                  onValueChange={(val) => setEditingNode({ ...editingNode, group: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="person">{t("person")}</SelectItem>
                    <SelectItem value="organization">{t("organization")}</SelectItem>
                    <SelectItem value="location">{t("location")}</SelectItem>
                    <SelectItem value="event">{t("event")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t("properties")}</Label>
                  <Button variant="outline" size="sm" onClick={addProperty}>
                    <i className="fa-solid fa-plus mr-2"></i>
                    {t("add")}
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {Object.entries(editingNode.properties).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <Input 
                        value={key}
                        className="w-1/3 h-8 text-sm"
                        onChange={(e) => {
                          const newProps = { ...editingNode.properties };
                          const oldValue = newProps[key];
                          delete newProps[key];
                          newProps[e.target.value] = oldValue;
                          setEditingNode({ ...editingNode, properties: newProps });
                        }}
                      />
                      <Input 
                        value={value}
                        className="flex-1 h-8 text-sm"
                        onChange={(e) => updateEditingProperty(key, e.target.value)}
                      />
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 shrink-0"
                        onClick={() => deleteProperty(key)}
                      >
                        <i className="fa-solid fa-trash text-xs text-destructive"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleSaveNode}>
              <i className="fa-solid fa-check mr-2"></i>
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Edge Dialog */}
      <Dialog open={editEdgeDialogOpen} onOpenChange={setEditEdgeDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fa-solid fa-link text-primary"></i>
              {t("editRelation") || "Edit Relation"}
            </DialogTitle>
            <DialogDescription>
              {t("editRelationDescription") || "Modify the relation label and properties"}
            </DialogDescription>
          </DialogHeader>
          
          {editingEdge && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edge-label">{t("relationLabel") || "Relation Label"}</Label>
                <Input 
                  id="edge-label" 
                  value={editingEdge.label} 
                  onChange={(e) => setEditingEdge({ ...editingEdge, label: e.target.value })}
                  placeholder="e.g., works_at, invested_in"
                />
              </div>
              
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">{t("source") || "From"}</p>
                  <p className="text-sm font-medium">{nodes.find(n => n.id === editingEdge.from)?.label}</p>
                </div>
                <i className="fa-solid fa-arrow-right text-muted-foreground"></i>
                <div className="flex-1 text-right">
                  <p className="text-xs text-muted-foreground">{t("target") || "To"}</p>
                  <p className="text-sm font-medium">{nodes.find(n => n.id === editingEdge.to)?.label}</p>
                </div>
              </div>

              <Separator />

              <Button 
                variant="destructive" 
                className="w-full"
                onClick={handleDeleteEdge}
              >
                <i className="fa-solid fa-trash mr-2"></i>
                {t("deleteRelation") || "Delete Relation"}
              </Button>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEdgeDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleSaveEdge}>
              <i className="fa-solid fa-check mr-2"></i>
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
